import {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import routes from "../../../../Routes/routes";
import HandleChange from "../../../../Helpers/HandleChange";
import {getUrlParameters, TAB_INDEX} from "../../../Dashboard/Visitor/External/Model/Model";
import {PublicValidateResidentOwner} from "../../../../reduxToolkit/Building/BuildingApi";

import {
    buildingRegistrationUrl,
    BUILDING_REGISTRATION_IDENTITY_MODEL,
    BUILDING_REGISTRATION_STEP_MODEL,
    resolveValidatedOwner
} from "../Model/Model";

/**
 *
 * Hook to handle the identify you step of the external building registration.
 * Holds the form of the invited resident, its validation and the navigation to
 * the identity validation flow.
 *
 * @author Karen Yunuen
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @returns {Object} An object containing state variables and handler functions:
 * - identityDetail: Form model with the data captured by the resident.
 * - emptyFields: Empty field validation flag.
 * - handleChange: Updates the form model.
 * - handleClickNext: Validates the form against the service and navigates to the identity validation.
 * - handleNavigateBack: Navigates back to the registration invitation.
 */
const BuildingRegistrationIdentifyYouApp = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {buildingId} = useParams();
    const [steps, setSteps] = useState(null);
    const [emptyFields, setEmptyFields] = useState(false);
    const [identityDetail, setIdentityDetail] = useState({...BUILDING_REGISTRATION_IDENTITY_MODEL});
    const {handleChange} = HandleChange({model: identityDetail, setModel: setIdentityDetail});

    const activeStep = TAB_INDEX.ONE;
    const title = 'lets_identify_you';
    const textBtnNext = 'continue';

    /**
     * Set initial steps when component mounts.
     *
     * @dependencies [] - Runs only once on mount.
     */
    useEffect(() => {
        setSteps({...BUILDING_REGISTRATION_STEP_MODEL.IDENTIFY_YOU});
    }, []);

    /**
     * Validates that the resident belongs to the building and to the apartment
     * they captured, and continues to the identity validation with the user the
     * service answers with. When that user already has their identity validated
     * the flow goes straight to the successfully verified screen.
     */
    const handleClickNext = () => {
        if (!identityDetail?.isValid()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);

        dispatch(PublicValidateResidentOwner(identityDetail.toValidateOwner(buildingId))).then(({payload}) => {
            resolveValidatedOwner({
                payload,
                buildingId,
                resident: {unit: identityDetail.unit, email: identityDetail.email},
                dispatch,
                navigate,
                t
            });
        });
    };

    /**
     * Navigate back to the registration invitation of the building. The user is
     * never carried back, the link that has one skips this form.
     */
    const handleNavigateBack = () => {
        navigate(getUrlParameters({
            url: buildingRegistrationUrl(routes.buildingRegistration.main, buildingId)
        }));
    };

    return {
        t,
        steps,
        activeStep,
        emptyFields,
        identityDetail,
        handleChange,
        handleClickNext,
        handleNavigateBack,
        title,
        textBtnNext
    };
};

export default BuildingRegistrationIdentifyYouApp;
